import React from 'react';
import {ArrowLeft} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-8 md:p-12">
        <Link to="/" className="inline-flex items-center text-green-700 hover:text-green-800 mb-8 transition-colors">
          <ArrowLeft className="h-5 w-5 mr-2" />
          Voltar ao Site
        </Link>

        <h1 className="text-4xl font-bold text-gray-900 mb-2">Política de Cookies</h1>
        <p className="text-sm text-gray-500 mb-8">Última atualização: {new Date().toLocaleDateString('pt-PT')}</p>

        <div className="prose prose-lg max-w-none space-y-8">
          {/* Seção 1 */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. O que são Cookies?</h2>
            <p className="text-gray-700 mb-4">
              Cookies são pequenos ficheiros de texto que são armazenados no seu dispositivo (computador, tablet ou telemóvel) 
              quando visita um website. Os cookies permitem que o website reconheça o seu dispositivo e memorize informações 
              sobre a sua visita, como as suas preferências e configurações.
            </p>
            <p className="text-gray-700">
              Os cookies facilitam a navegação e tornam os websites mais úteis, personalizando a experiência do utilizador.
            </p>
          </section>

          {/* Seção 2 */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Como Utilizamos os Cookies</h2>
            <p className="text-gray-700 mb-4">
              O website da Pinhacorada utiliza cookies para melhorar a sua experiência de navegação e garantir o funcionamento 
              adequado das funcionalidades do site.
            </p>
          </section>

          {/* Seção 3 */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Tipos de Cookies Utilizados</h2>
            
            <div className="space-y-6">
              {/* Cookies Essenciais */}
              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-600">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">🔒 Cookies Essenciais (Estritamente Necessários)</h3>
                <p className="text-gray-700 mb-3">
                  Estes cookies são fundamentais para o funcionamento do website e não podem ser desativados nos nossos sistemas.
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Permitem a navegação no website</li>
                  <li>Garantem a segurança e funcionamento das funcionalidades básicas</li>
                  <li>Não recolhem informação pessoal identificável</li>
                </ul>
                <p className="text-sm text-gray-600 mt-3 italic">
                  Duração: Sessão (eliminados quando fecha o navegador)
                </p>
              </div>

              {/* Cookies Funcionais */}
              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-600">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">⚙️ Cookies Funcionais</h3>
                <p className="text-gray-700 mb-3">
                  Estes cookies permitem melhorar a funcionalidade e personalização do website, 
                  como lembrar as suas preferências de navegação.
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Memorizam as suas escolhas (idioma, região)</li>
                  <li>Melhoram a sua experiência de navegação</li>
                  <li>Permitem funcionalidades melhoradas e personalizadas</li>
                </ul>
                <p className="text-sm text-gray-600 mt-3 italic">
                  Duração: Até 12 meses
                </p>
              </div>

              {/* Cookies de Desempenho */}
              <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-600">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">📊 Cookies de Desempenho e Análise</h3>
                <p className="text-gray-700 mb-3">
                  Estes cookies ajudam-nos a compreender como os visitantes interagem com o nosso website, 
                  fornecendo informações sobre as áreas visitadas, o tempo de permanência e eventuais problemas encontrados.
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Contabilizam visitas e fontes de tráfego</li>
                  <li>Ajudam-nos a melhorar o desempenho do website</li>
                  <li>Todas as informações são anónimas</li>
                </ul>
                <p className="text-sm text-gray-600 mt-3 italic">
                  Duração: Até 24 meses
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>Nota:</strong> Estes cookies apenas são instalados com o seu consentimento explícito.
                </p>
              </div>
            </div>
          </section>

          {/* Seção 4 */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Cookies de Terceiros</h2>
            <p className="text-gray-700 mb-4">
              Em alguns casos, utilizamos cookies fornecidos por terceiros de confiança. O nosso website pode utilizar:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Google Analytics:</strong> Para análise de tráfego e comportamento dos visitantes (apenas com consentimento)</li>
                <li><strong>Redes Sociais:</strong> Se partilhar conteúdo através de botões de redes sociais, essas plataformas podem definir cookies</li>
              </ul>
            </div>
            <p className="text-gray-700 mt-4">
              Estes terceiros têm as suas próprias políticas de privacidade e cookies sobre as quais não temos controlo.
            </p>
          </section>

          {/* Seção 5 */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Gestão e Controlo de Cookies</h2>
            <p className="text-gray-700 mb-4">
              Pode controlar e/ou eliminar cookies conforme desejar. Tem as seguintes opções:
            </p>
            
            <div className="bg-green-50 p-6 rounded-lg space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">✅ Através do Nosso Website</h3>
                <p className="text-gray-700">
                  Pode aceitar ou recusar cookies não essenciais através do banner de cookies apresentado na sua primeira visita.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">🌐 Através do Navegador</h3>
                <p className="text-gray-700 mb-2">
                  Pode configurar o seu navegador para bloquear ou eliminar cookies. Consulte as instruções do seu navegador:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-green-700 underline hover:text-green-800">Google Chrome</a></li>
                  <li><a href="https://support.mozilla.org/pt-PT/kb/limpar-cookies-e-dados-de-sites-no-firefox" target="_blank" rel="noopener noreferrer" className="text-green-700 underline hover:text-green-800">Mozilla Firefox</a></li>
                  <li><a href="https://support.apple.com/pt-pt/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-green-700 underline hover:text-green-800">Safari</a></li>
                  <li><a href="https://support.microsoft.com/pt-pt/windows/eliminar-e-gerir-cookies-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" className="text-green-700 underline hover:text-green-800">Microsoft Edge</a></li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-4 mt-4">
              <p className="text-gray-700">
                ⚠️ <strong>Atenção:</strong> Bloquear ou eliminar cookies pode afetar a funcionalidade do website e impedir o acesso a determinadas funcionalidades.
              </p>
            </div>
          </section>

          {/* Seção 6 */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Consentimento</h2>
            <p className="text-gray-700 mb-4">
              Ao continuar a navegar no nosso website após a apresentação do aviso de cookies, está a consentir 
              a utilização de cookies essenciais e funcionais.
            </p>
            <p className="text-gray-700">
              Para cookies de análise e desempenho, solicitamos o seu consentimento explícito através do banner de cookies. 
              Pode retirar o seu consentimento a qualquer momento através das definições do seu navegador.
            </p>
          </section>

          {/* Seção 7 */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Atualizações à Política de Cookies</h2>
            <p className="text-gray-700">
              A Pinhacorada reserva-se o direito de atualizar esta Política de Cookies periodicamente para refletir 
              alterações nas práticas de cookies ou por motivos legais ou regulamentares. 
              Recomendamos que consulte esta página regularmente para se manter informado sobre como utilizamos cookies.
            </p>
          </section>

          {/* Seção 8 */}
          <section className="border-t pt-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Contactos</h2>
            <p className="text-gray-700 mb-4">
              Se tiver questões sobre a nossa utilização de cookies, contacte-nos através de:
            </p>
            <div className="bg-gray-900 text-white p-6 rounded-lg">
              <p className="mb-2">🏢 <strong>Pinhacorada</strong></p>
              <p className="mb-2">📍 Mortágua, Portugal</p>
              <p className="mb-2">📧 Email: <a href="mailto:pinhacorada@gmail.com" className="underline hover:text-green-400">pinhacorada@gmail.com</a></p>
              <p>📞 Telefone: 965 812 210</p>
            </div>
          </section>

          {/* Links Úteis */}
          <section className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">🔗 Links Úteis</h3>
            <ul className="space-y-2 text-gray-700">
              <li>
                <Link to="/politica-privacidade" className="text-green-700 underline hover:text-green-800">
                  Consulte a nossa Política de Privacidade
                </Link>
              </li>
              <li>
                <a href="https://www.cnpd.pt" target="_blank" rel="noopener noreferrer" className="text-green-700 underline hover:text-green-800">
                  Comissão Nacional de Proteção de Dados (CNPD)
                </a>
              </li>
              <li>
                <a href="https://www.allaboutcookies.org/pt/" target="_blank" rel="noopener noreferrer" className="text-green-700 underline hover:text-green-800">
                  Mais informações sobre cookies
                </a>
              </li>
            </ul>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t">
          <Link to="/" className="inline-flex items-center text-green-700 hover:text-green-800 transition-colors font-semibold">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Voltar ao Site
          </Link>
        </div>
      </div>
    </div>
  );
}
